<template>
  <div>
    <Breadcrumbs main="Admin" title="Clients" />
    <div class="container-fluid">
      <div class="row">
        <div class="col-12">
          <div class="card">
            <div class="card-header d-flex justify-content-between align-items-center">
              <div>
                <h5 class="mb-0">Clients</h5>
                <span class="text-muted">Manage law firm clients</span>
              </div>
              <div class="d-flex gap-2">
                <button
                  class="btn btn-outline-primary btn-sm"
                  type="button"
                  :disabled="loading"
                  @click="loadClients"
                >
                  <vue-feather type="refresh-cw" class="me-1" />
                  Refresh
                </button>
                <button
                  v-if="canAdd"
                  class="btn btn-primary"
                  type="button"
                  @click="openCreate"
                >
                  <vue-feather type="plus" class="me-1" />
                  Add Client
                </button>
              </div>
            </div>
            <div class="card-body">
              <div v-if="error" class="alert alert-danger" role="alert">
                {{ error }}
              </div>
              <div v-if="loading" class="text-center py-4 text-muted">
                Loading clients...
              </div>
              <div v-else-if="clients.length === 0" class="text-center py-4 text-muted">
                No clients found.
              </div>
              <div v-else class="table-responsive">
                <table class="table table-hover">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Name</th>
                      <th>Phone</th>
                      <th>Email</th>
                      <th>Address</th>
                      <th>Added</th>
                      <th class="text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="client in clients" :key="client.clientId">
                      <td>{{ client.clientId }}</td>
                      <td><strong>{{ client.fullName || '—' }}</strong></td>
                      <td>{{ client.phone || '—' }}</td>
                      <td>{{ client.email || '—' }}</td>
                      <td>{{ client.address || '—' }}</td>
                      <td>{{ formatDate(client.createdAt) }}</td>
                      <td class="text-end">
                        <button
                          v-if="canEdit"
                          class="btn btn-sm btn-outline-primary me-1"
                          type="button"
                          title="Edit"
                          @click="openEdit(client)"
                        >
                          <vue-feather type="edit-2" />
                        </button>
                        <button
                          v-if="canDelete"
                          class="btn btn-sm btn-outline-danger"
                          type="button"
                          title="Delete"
                          @click="confirmDelete(client)"
                        >
                          <vue-feather type="trash-2" />
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <teleport to="body">
      <div
        v-if="showFormModal"
        class="modal fade show d-block"
        tabindex="-1"
        role="dialog"
        aria-modal="true"
      >
        <div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable" role="document">
          <div class="modal-content">
            <form @submit.prevent="submitForm">
              <div class="modal-header">
                <h5 class="modal-title">{{ isEditing ? 'Edit Client' : 'Add Client' }}</h5>
                <button type="button" class="btn-close" aria-label="Close" @click="closeFormModal" />
              </div>
              <div class="modal-body">
                <div v-if="formError" class="alert alert-danger" role="alert">
                  {{ formError }}
                </div>
                <div class="row">
                  <div class="col-md-6">
                    <div class="form-group form-field mb-2">
                      <label class="form-label">Full name</label>
                      <input v-model="form.fullName" class="form-control" type="text">
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="form-group form-field mb-2">
                      <label class="form-label">Phone</label>
                      <input
                        v-model="form.phone"
                        class="form-control"
                        type="text"
                        maxlength="30"
                      >
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="form-group form-field mb-2">
                      <label class="form-label">Email</label>
                      <input
                        v-model="form.email"
                        class="form-control"
                        type="email"
                        maxlength="100"
                      >
                    </div>
                  </div>
                  <div class="col-12">
                    <div class="form-group form-field mb-2">
                      <label class="form-label">Address</label>
                      <textarea
                        v-model="form.address"
                        class="form-control"
                        rows="2"
                        maxlength="255"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div class="modal-footer">
                <button type="button" class="btn btn-secondary" @click="closeFormModal">
                  Cancel
                </button>
                <button type="submit" class="btn btn-primary" :disabled="saving">
                  {{ saving ? 'Saving...' : (isEditing ? 'Save Changes' : 'Create Client') }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      <div v-if="showFormModal" class="modal-backdrop fade show" @click="closeFormModal" />
    </teleport>

    <teleport to="body">
      <div
        v-if="clientToDelete"
        class="modal fade show d-block"
        tabindex="-1"
        role="dialog"
        aria-modal="true"
      >
        <div class="modal-dialog modal-dialog-centered" role="document">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">Delete Client</h5>
              <button type="button" class="btn-close" aria-label="Close" @click="clientToDelete = null" />
            </div>
            <div class="modal-body">
              <p class="mb-2">
                Delete <strong>{{ clientToDelete.fullName || `Client #${clientToDelete.clientId}` }}</strong>?
              </p>
              <p class="text-muted mb-0 f-12">
                The client will be soft-deleted and hidden from the list. The record is retained for audit.
              </p>
              <div v-if="actionError" class="alert alert-danger mt-3 mb-0" role="alert">
                {{ actionError }}
              </div>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" @click="clientToDelete = null">
                Cancel
              </button>
              <button type="button" class="btn btn-danger" :disabled="actionLoading" @click="submitDelete">
                {{ actionLoading ? 'Deleting...' : 'Delete' }}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div v-if="clientToDelete" class="modal-backdrop fade show" @click="clientToDelete = null" />
    </teleport>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import {
  listClients,
  createClient,
  updateClient,
  deleteClient,
} from '@/services/clientService'

function hasClientPrivilege(user, action) {
  if (!user?.privileges?.length) return true
  const clientPriv = user.privileges.find((p) => p.entity === 'CLIENT')
  if (!clientPriv) return false
  const map = {
    view: 'canView',
    add: 'canAdd',
    edit: 'canEdit',
    delete: 'canDelete',
  }
  return clientPriv[map[action]] === true
}

const emptyForm = () => ({
  fullName: '',
  phone: '',
  email: '',
  address: '',
})

export default {
  computed: {
    ...mapGetters('auth', ['currentUser']),

    canAdd() {
      return hasClientPrivilege(this.currentUser, 'add')
    },

    canEdit() {
      return hasClientPrivilege(this.currentUser, 'edit')
    },

    canDelete() {
      return hasClientPrivilege(this.currentUser, 'delete')
    },
  },

  data() {
    return {
      clients: [],
      loading: false,
      error: '',
      showFormModal: false,
      isEditing: false,
      editingId: null,
      form: emptyForm(),
      formError: '',
      saving: false,
      clientToDelete: null,
      actionError: '',
      actionLoading: false,
    }
  },

  created() {
    this.loadClients()
  },

  methods: {
    async loadClients() {
      this.loading = true
      this.error = ''

      try {
        const data = await listClients()
        this.clients = Array.isArray(data) ? data : []
      } catch (err) {
        if (err.status === 403) {
          this.error = 'You do not have permission to view clients.'
        } else {
          this.error = err.message || 'Failed to load clients'
        }
      } finally {
        this.loading = false
      }
    },

    formatDate(value) {
      if (!value) return '—'
      return new Date(value).toLocaleDateString()
    },

    openCreate() {
      this.isEditing = false
      this.editingId = null
      this.form = emptyForm()
      this.formError = ''
      this.showFormModal = true
    },

    openEdit(client) {
      this.isEditing = true
      this.editingId = client.clientId
      this.form = {
        fullName: client.fullName || '',
        phone: client.phone || '',
        email: client.email || '',
        address: client.address || '',
      }
      this.formError = ''
      this.showFormModal = true
    },

    closeFormModal() {
      if (this.saving) return
      this.showFormModal = false
      this.formError = ''
    },

    buildPayload() {
      const payload = {}

      const fullName = this.form.fullName?.trim()
      if (fullName) payload.fullName = fullName

      const phone = this.form.phone?.trim()
      if (phone) payload.phone = phone

      const email = this.form.email?.trim()
      if (email) payload.email = email

      const address = this.form.address?.trim()
      if (address) payload.address = address

      return payload
    },

    async submitForm() {
      this.formError = ''
      this.saving = true

      try {
        if (this.isEditing) {
          await updateClient({
            clientId: this.editingId,
            ...this.buildPayload(),
          })
        } else {
          await createClient(this.buildPayload())
        }
        this.showFormModal = false
        await this.loadClients()
      } catch (err) {
        this.formError = err.message || 'Request failed'
      } finally {
        this.saving = false
      }
    },

    confirmDelete(client) {
      this.clientToDelete = client
      this.actionError = ''
    },

    async submitDelete() {
      this.actionError = ''
      this.actionLoading = true

      try {
        await deleteClient(this.clientToDelete.clientId)
        this.clientToDelete = null
        await this.loadClients()
      } catch (err) {
        this.actionError = err.message || 'Failed to delete client'
      } finally {
        this.actionLoading = false
      }
    },
  },
}
</script>
